import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { photographyShowcaseHref, useHomeHeroSlides } from "../../hooks/useHomeHeroSlides";

export default function HeroCarousel() {
  const { slides, loading } = useHomeHeroSlides();
  const [current, setCurrent] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    setCurrent(0);
  }, [slides]);

  const go = useCallback(
    (dir) => {
      setCurrent((prev) => (prev + dir + slides.length) % slides.length);
    },
    [slides.length]
  );

  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    if (slides.length === 0) {
      return;
    }
    timerRef.current = setInterval(() => {
      setCurrent((prev) => (prev + 1) % slides.length);
    }, 6500);
  }, [slides.length]);

  useEffect(() => {
    resetTimer();
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [resetTimer]);

  if (loading) {
    return <section className="plate-hero" aria-busy="true" aria-label="Loading photographs" />;
  }

  if (slides.length === 0) {
    return null;
  }

  const handlePrev = () => {
    go(-1);
    resetTimer();
  };

  const handleNext = () => {
    go(+1);
    resetTimer();
  };

  const slide = slides[current];

  return (
    <section className="plate-hero" aria-label="Featured photographs">
      <div className="plate-hero__slides">
        {slides.map((item, index) => {
          const focusX = item.focusX ?? 50;
          const focusY = item.focusY ?? 50;
          const isActive = index === current;
          const className = `plate-hero__slide${isActive ? " is-active plate-hero__slide--advance" : ""}`;
          const style = {
            backgroundImage: `url('${item.src}')`,
            backgroundPosition: `${focusX}% ${focusY}%`,
          };

          if (isActive) {
            return (
              <button
                key={item.id || item.src + index}
                type="button"
                className={className}
                style={style}
                aria-label="Next photograph"
                onClick={handleNext}
              />
            );
          }

          return (
            <div
              key={item.id || item.src + index}
              className={className}
              style={style}
              aria-hidden
            />
          );
        })}
      </div>

      <div className="plate-hero__gradient" aria-hidden="true" />

      <div className="plate-hero__roles" id="hero-roles">
        <span />
        <span>MY JOURNEY THUS FAR</span>
        <span />
      </div>

      <button
        type="button"
        className="plate-hero__arrow plate-hero__arrow--prev"
        aria-label="Previous photograph"
        onClick={handlePrev}
      >
        ‹
      </button>
      <button
        type="button"
        className="plate-hero__arrow plate-hero__arrow--next"
        aria-label="Next photograph"
        onClick={handleNext}
      >
        ›
      </button>

      <div className="plate-hero__bottom">
        <div className="plate-hero__bottom-spacer" />
        <a href="#explore" className="plate-hero__scroll-cue" aria-label="Scroll to explore">
          ↓
        </a>
        {slide.cap ? (
          <Link
            key={slide.id}
            to={photographyShowcaseHref({ id: slide.id, t: slide.cap })}
            className="plate-hero__caption plate-hero__caption--link"
          >
            [ {slide.cap} ]
          </Link>
        ) : null}
      </div>
    </section>
  );
}
